const ENROL_NOW_LINK = '/signup';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

interface ClickCTAProps {
  link_text: string;
  link_classes?: string;
  link_url?: string;
}

interface ClickCampaignProps {
  link_text: string;
  link_url?: string;
}

export enum DATALAYER_EVENT {
  PAGE_VIEW = 'page_view',
  CLICK_CAMPAIGN = 'click_campaign',
  CLICK_CTA = 'click_cta',
  COMPLETE_FORM = 'complete_form',
  CLICK_MENU_NAV = 'click_menu_nav',
  CLICK_HEADER_NAV = 'click_header_nav',
  CLICK_FOOTER_NAV = 'click_footer_nav',
  CLICK_CAROUSEL = 'click_carousel',
  CLICK_ACCORDION = 'click_accordion'
}

export const getMixpanelEventRoute = (route?: string) => {
  const rootURL = window.location.origin;
  return route ? `${rootURL}${route}` : `${rootURL}/`;
};

export const getCurrentEnvironmentDomain = () => {
  let hostName = window.location.hostname;
  switch (hostName) {
    case 'localhost':
      return 'localhost:3000';
    case 'test.breakingtheice.ca':
      return 'https://test.breakingtheice.ca';
    case 'staging.breakingtheice.ca':
      return 'https://staging.breakingtheice.ca';
    case 'breakingtheice.ca':
      return 'https://breakingtheice.ca';
    default:
      return window.location.origin;
  }
};

const enum ENV {
  LOCAL = 'local',
  PROD = 'prod',
  TEST = 'test',
  STAGING = 'staging'
}

export const getEnv = () => {
  let hostName = window.location.hostname;
  switch (hostName) {
    case 'localhost':
      return ENV.LOCAL;
    case 'test.breakingtheice.ca':
      return ENV.TEST;
    case 'staging.breakingtheice.ca':
      return ENV.STAGING;
    case 'breakingtheice.ca':
      return ENV.PROD;
    default:
      return ENV.PROD;
  }
};

const DataLayer = {
  lastUri: '',
  referringDomain: '',

  pushEvent(eventName: DATALAYER_EVENT, data: any) {
    data.event = eventName;
    data.location = `${getCurrentEnvironmentDomain()}${window.location.pathname}${window.location.search}${window.location.hash}`;
    this._push(data);
  },

  //Should only be used internally to this file, use pushEvent instead
  _push(data: any) {
    if (!(getEnv() === ENV.PROD)) {
      console.log(`LOCAL ENVIRONMENT: DataLayer internal event created`);
      console.log(`LOCAL ENVIRONMENT: ${JSON.stringify(data)}`);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  },

  pushRouteChange() {
    // The title will be the Page title
    const title = window.document.title;

    const pageViewPayLoad: any = {
      current_uri: window.location.href,
      last_uri: DataLayer.lastUri,
      page_title: title,

      $referrer: document.referrer
    };

    // Analytics team want these ($referrer, $referring_domain) to be the internal site after the first page view.
    // ie. Navigating to subsequent pages after the first page_view should reflect the internal site domain + uri. We set them below after the first page view.
    if (DataLayer.lastUri) pageViewPayLoad.$referrer = DataLayer.lastUri; //will be document.referrer for first navigate/page view, then lastUri after each internal page nav
    if (DataLayer.referringDomain) pageViewPayLoad.$referring_domain = DataLayer.referringDomain; //similar to above

    this.pushEvent(DATALAYER_EVENT.PAGE_VIEW, pageViewPayLoad);

    //Set the lastUri + referringDomain for subsequent page views.
    DataLayer.lastUri = window.location.href;
    DataLayer.referringDomain = window.location.host;
  },

  pushClickCampaign(clickCampaignVariables: ClickCampaignProps) {
    this.pushEvent(DATALAYER_EVENT.CLICK_CAMPAIGN, {
      ...clickCampaignVariables
    });
  },

  pushClickEnrolButton(enrollButtonText = 'Enroll', to = ENROL_NOW_LINK) {
    this.pushEvent(DATALAYER_EVENT.CLICK_CTA, {
      link_text: enrollButtonText,
      link_url: to
    });
  },

  pushClickLoginButton(to: string) {
    this.pushEvent(DATALAYER_EVENT.CLICK_CTA, {
      link_text: 'Login',
      link_url: to
    });
  },

  pushClickCarousel(type: 'forward' | 'backward', index?: number) {
    const payload = {
      type: type,
      index: index
    };
    this.pushEvent(DATALAYER_EVENT.CLICK_CAROUSEL, payload);
  },

  pushClickAccordian(id: number, name: string, type: 'open' | 'close', linkClasses?: any) {
    const payload = {
      id: id,
      type: type,
      link_classes: linkClasses,
      name: name
    };
    this.pushEvent(DATALAYER_EVENT.CLICK_ACCORDION, payload);
  },

  pushClickCTA(text: string, url?: string, classes?: string) {
    const clickData: ClickCTAProps = {
      link_text: text,
      link_url: getMixpanelEventRoute(url),
      link_classes: classes
    };

    this.pushEvent(DATALAYER_EVENT.CLICK_CTA, {
      ...clickData
    });
  }
};

export default DataLayer;
